"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DurableSocket = void 0;
/**
 * Provides a durable WebSocket. Such a socket will automatically handle reconnection including
 * exponential backoff and jitter. It will also enqueue messages while the socket is down and
 * send them once connection is restored.
 *
 * Drop in compatible with WebSocket, with some additional events:
 * - 'lost': fired when the connection goes down
 * - 'restore': fired when the connection is restored after being down
 *
 * The standard open and close events only fire when the connection is initially established
 * and when the socket is intentionally ended by calling close().
 */
var DurableSocket = /** @class */ (function () {
    function DurableSocket(url, protocols) {
        this.url = url;
        this.protocols = protocols;
        this.lastPong = 0;
        this.enablePing = true;
        this.pingInterval = 10000;
        this.pingKeepAliveInterval = 25000;
        this._closed = false;
        this._open = false;
        this.reconnectTime = 500; // 2
        this.maxReconnectTime = 30000; // 10000
        this.maxAttempts = 0;
        this.jitter = 0.05;
        this._ready = false;
        this._messageQueue = [];
        this._attempt = 0;
        this._subscribers = new Map();
        this.connect();
    }
    DurableSocket.prototype.connect = function () {
        var _this = this;
        var connected = false;
        this._socket = new WebSocket(this.url, this.protocols);
        this._socket.onopen = function (ev) { return (connected = true, _this.handleConnect(ev)); };
        this._socket.onerror = function (ev) { return _this.dispatchEvent(ev); };
        this._socket.onclose = function (ev) { return _this.handleLost(); };
        this._socket.onmessage = function (ev) { return _this.handleMessage(ev); };
    };
    DurableSocket.prototype.handleConnect = function (ev) {
        var _this = this;
        var first = !this._open;
        if (first) {
            this.dispatchEvent(ev);
            this._open = true;
        }
        this._ready = true;
        this._attempt = 0;
        this._messageQueue.splice(0).forEach(function (m) { return _this._socket.send(m); });
        if (!first) {
            console.log("[Socket] Connection Restored [".concat(this.url, "]"));
            this.dispatchEvent({
                type: 'restore',
                bubbles: false,
                cancelable: false,
                cancelBubble: false,
                composed: false,
                currentTarget: this,
                defaultPrevented: false,
                eventPhase: 0,
                isTrusted: true,
                returnValue: undefined,
                srcElement: undefined,
                target: this,
                timeStamp: Date.now(),
                composedPath: function () { return []; },
                initEvent: undefined,
                preventDefault: function () { this.defaultPrevented = true; },
                stopPropagation: function () { },
                stopImmediatePropagation: function () { },
                AT_TARGET: 2,
                BUBBLING_PHASE: 3,
                CAPTURING_PHASE: 1,
                NONE: 0, //Event.NONE
            });
        }
        this.lastPong = Date.now();
        if (this.enablePing)
            this.pingTimer = setInterval(function () {
                var _a, _b;
                if (_this._closed) {
                    clearInterval(_this.pingTimer);
                    return;
                }
                try {
                    _this.send(JSON.stringify({ type: 'ping' }));
                }
                catch (e) {
                    console.error("[Socket] Failed to send ping message. Assuming connection is broken. [".concat(_this.url, "]"));
                    try {
                        (_a = _this._socket) === null || _a === void 0 ? void 0 : _a.close();
                    }
                    catch (e) {
                        console.error("[Socket] Failed to close socket after ping failure: ".concat(e.message, " [").concat(_this.url, "]"));
                    }
                    return;
                }
                if (_this.lastPong < Date.now() - _this.pingKeepAliveInterval) {
                    console.log("[Socket] No keep-alive response in ".concat(_this.pingKeepAliveInterval, "ms. Forcing reconnect... [").concat(_this.url, "]"));
                    try {
                        (_b = _this._socket) === null || _b === void 0 ? void 0 : _b.close();
                    }
                    catch (e) {
                        console.error("[Socket] Failed to close socket after timeout waiting for pong: ".concat(e.message, " [").concat(_this.url, "]"));
                    }
                }
            }, this.pingInterval);
    };
    DurableSocket.prototype.handleMessage = function (ev) {
        var message = JSON.parse(ev.data);
        if (message.type === 'pong') {
            this.lastPong = Date.now();
            return;
        }
        this.dispatchEvent(ev);
    };
    DurableSocket.prototype.handleLost = function () {
        var _a;
        if (this._closed)
            return;
        if (this._ready) {
            console.log("[Socket] Connection Lost [".concat(this.url, "]"));
        }
        this._ready = false;
        this._attempt += 1;
        clearInterval(this.pingTimer);
        this.pingTimer = null;
        (_a = this._socket) === null || _a === void 0 ? void 0 : _a.close();
        this._socket = null;
        this.dispatchEvent({
            type: 'lost',
            bubbles: false,
            cancelable: false,
            cancelBubble: false,
            composed: false,
            currentTarget: this,
            defaultPrevented: false,
            eventPhase: 0,
            isTrusted: true,
            returnValue: undefined,
            srcElement: undefined,
            target: this,
            timeStamp: Date.now(),
            composedPath: function () { return []; },
            initEvent: undefined,
            preventDefault: function () { this.defaultPrevented = true; },
            stopPropagation: function () { },
            stopImmediatePropagation: function () { },
            AT_TARGET: 2,
            BUBBLING_PHASE: 3,
            CAPTURING_PHASE: 1,
            NONE: 0, //Event.NONE
        });
        if (this.maxAttempts > 0 && this._attempt >= this.maxAttempts) {
            this.close(503, 'Service Unavailable');
            return;
        }
        this.reconnect();
    };
    Object.defineProperty(DurableSocket.prototype, "actualReconnectTime", {
        get: function () {
            var reconnectTime = Math.min(this.maxReconnectTime, this.reconnectTime * this._attempt * 1.5);
            return Math.min(this.maxReconnectTime, reconnectTime + Math.random() * this.jitter * reconnectTime);
        },
        enumerable: false,
        configurable: true
    });
    DurableSocket.prototype.reconnect = function () {
        var _this = this;
        console.log("[Socket] Waiting ".concat(this.actualReconnectTime, "ms before reconnect (attempt ").concat(this._attempt, ") [").concat(this.url, "]"));
        setTimeout(function () { return _this.connect(); }, this.actualReconnectTime);
    };
    Object.defineProperty(DurableSocket.prototype, "binaryType", {
        get: function () { return this._socket.binaryType; },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "bufferedAmount", {
        get: function () { return this._socket.bufferedAmount; },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "extensions", {
        get: function () { return this._socket.extensions; },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "CLOSED", {
        get: function () { return WebSocket.CLOSED; },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "CLOSING", {
        get: function () { return WebSocket.CLOSING; },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "CONNECTING", {
        get: function () { return WebSocket.CONNECTING; },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "OPEN", {
        get: function () { return WebSocket.OPEN; },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "onclose", {
        get: function () { return this._onclose; },
        set: function (value) {
            if (this._onclose)
                this.removeEventListener('close', this._onclose);
            this._onclose = value;
            if (value)
                this.addEventListener('close', value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "onerror", {
        get: function () { return this._onerror; },
        set: function (value) {
            if (this._onclose)
                this.removeEventListener('error', this._onerror);
            this._onerror = value;
            if (value)
                this.addEventListener('error', value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "onmessage", {
        get: function () { return this._onmessage; },
        set: function (value) {
            if (this._onclose)
                this.removeEventListener('message', this._onmessage);
            this._onmessage = value;
            if (value)
                this.addEventListener('message', value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "onopen", {
        get: function () { return this._onopen; },
        set: function (value) {
            if (this._onclose)
                this.removeEventListener('open', this._onopen);
            this._onopen = value;
            if (value)
                this.addEventListener('open', value);
        },
        enumerable: false,
        configurable: true
    });
    ;
    Object.defineProperty(DurableSocket.prototype, "protocol", {
        get: function () { return this._socket.protocol; },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DurableSocket.prototype, "readyState", {
        get: function () { return this._socket.readyState; },
        enumerable: false,
        configurable: true
    });
    DurableSocket.prototype.close = function (code, reason) {
        var _a;
        this._closed = true;
        (_a = this._socket) === null || _a === void 0 ? void 0 : _a.close(code, reason);
        this._socket = null;
        this.dispatchEvent({
            type: 'close',
            bubbles: false,
            cancelable: false,
            cancelBubble: false,
            composed: false,
            currentTarget: this,
            defaultPrevented: false,
            eventPhase: Event.NONE,
            isTrusted: true,
            returnValue: undefined,
            srcElement: undefined,
            target: this,
            timeStamp: Date.now(),
            composedPath: function () { return []; },
            initEvent: undefined,
            preventDefault: function () { this.defaultPrevented = true; },
            stopPropagation: function () { },
            stopImmediatePropagation: function () { },
            AT_TARGET: Event.AT_TARGET,
            BUBBLING_PHASE: Event.BUBBLING_PHASE,
            CAPTURING_PHASE: Event.CAPTURING_PHASE,
            NONE: Event.NONE
        });
    };
    DurableSocket.prototype.send = function (data) {
        if (!this._ready) {
            this._messageQueue.push(data);
        }
        else {
            this._socket.send(data);
        }
    };
    DurableSocket.prototype.addEventListener = function (type, listener, options) {
        var _a;
        this._subscribers.set(type, __spreadArray(__spreadArray([], ((_a = this._subscribers.get(type)) !== null && _a !== void 0 ? _a : []), true), [listener], false));
    };
    DurableSocket.prototype.removeEventListener = function (type, listener, options) {
        var _a;
        this._subscribers.set(type, __spreadArray([], ((_a = this._subscribers.get(type)) !== null && _a !== void 0 ? _a : []).filter(function (x) { return x !== listener; }), true));
    };
    DurableSocket.prototype.dispatchEvent = function (event) {
        var _a;
        var subs = (_a = this._subscribers.get(event.type)) !== null && _a !== void 0 ? _a : [];
        subs.forEach(function (f) { return f(event); });
        return !event.defaultPrevented;
    };
    return DurableSocket;
}());
exports.DurableSocket = DurableSocket;
